import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  WorkplaceBootstrapDto,
  WorkplaceProfileSummaryDto,
  WorkplacePresenceSummaryDto,
  UserStatus,
  WorkplaceOrgSummaryDto,
} from 'nexgen-types';
import { useAppSelectionStore } from '@host/stores';
import { useMyFloorStore } from '@/apps/office/stores/myFloorStore';
import { DEFAULT_BG, DEFAULT_AVATAR } from '@host/workplace/services/WorkplaceService';
import { WorkplaceEventRouter } from '@host/workplace/events/WorkplaceEventRouter';
import { ApiService } from '@shared/services/api.service';
import { OfficeService } from '@/apps/office/services/OfficeService';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { useFloorManagerStore } from '@/apps/office/stores/FloorManagerStore';
import useChatStore from '@/apps/chat/store/chat.store';
import { Logger } from '../../shared/services/logger.service';
import posthog, { isPostHogEnabled } from '@host/config/posthog';

// Only a masked name leaves the app for analytics, e.g. "Vikas Batra" -> "Vi*** Ba***".
// At most half of each part is kept, so short parts stay hidden too ("Li" -> "L*").
// Counted in code points, not UTF-16 units, so a surrogate pair is never split in half.
const maskName = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => {
      const chars = Array.from(part);
      const visible = Math.min(2, Math.floor(chars.length / 2));
      return chars.slice(0, visible).join('') + '*'.repeat(chars.length - visible);
    })
    .join(' ');

interface Occupant {
  pid: string;
  name: string;
  avatar_url: string;
  avatar_thumbnail_url?: string | null;
}

interface ThemeSettings {
  backgroundColor: string;
  backgroundOpacity: number;
  text: string;
}

interface DrawerView {
  appId: string;
  viewName: string;
  title: string;
}

interface Guest {
  guest_pid: string;
  guest_name: string;
  room_pid: string;
  /** Type of the room the guest is placed in; older API versions do not send it. */
  room_type?: string | null;
}

// Auditorium attendees are placed with the session host as their "host", but they are
// not the host's visitors — they must never reach the guest-management widget. The API
// already leaves them out; this keeps the widget right even if one slips through.
const isHostManagedGuest = (guest: Guest): boolean => guest?.room_type !== 'auditorium';

export interface UserContext {
  user_pid: string;
  room_pid: string;
  floor_pid: string;
  org_pid: string;
}

export interface WorkplaceState {
  org: WorkplaceOrgSummaryDto;
  profile: WorkplaceProfileSummaryDto;
  presence: WorkplacePresenceSummaryDto;
  orgSettings: {
    'live-caption-settings'?: {
      speechModel: 'universal-streaming-english' | 'universal-streaming-multilingual';
      translationEnabled: boolean;
      /** Target languages this org allows translation into. */
      translationLanguages?: string[];
    };
    [key: string]: any;
  };
  isIdle: boolean;
  hasInitialize: boolean;
  currentRoom: {
    pid: string;
    name: string;
    floor_pid: string;
    background_url: string;
    number?: number;
    occupants: Occupant[];
    displayName?: string;
    type?: string;
  };
  user: {
    pid: string;
    name: string;
    avatar_url: string;
    avatar_thumbnail_url?: string | null;
    timezone: string;
    status: string;
  };
  homeRoom: {
    pid: string;
    name: string;
    floor_pid: string;
    background_url: string;
    occupants: Occupant[];
  };
  guestUsers: Guest[];
  applications: { uuid: string; name: string }[];
  currentSlots: {
    full: string | null;
    panelA: string | null;
    panelB: string | null;
    drawer: string | null;
  };
  floatingPanels: Record<string, string | null>;
  appSelections: Record<string, string>;
  layoutMode: 'one-column' | 'two-column';
  drawerOpen: boolean;
  drawerWidth: number;
  panelAWidth: number;
  drawerView: DrawerView | null;
  displayTimezone: string;
  theme: {
    topbar: ThemeSettings;
    appbar: ThemeSettings;
  };
  showRightSidebar: boolean;
  showMobileSideMenu: boolean;
  showAdminView: boolean;
  showReportsView: boolean;
  showLeftPanel: boolean;
  showApphubLeftPanel: boolean;
  showBookmarkLeftPanel: boolean;
  showOfficeLeftPanel: boolean;
  /** NGE-1078: the auditorium sessions panel (scheduling + the floor's bookings). */
  showAuditoriumLeftPanel: boolean;
  showNexgenCxLeftPanel: boolean;
  showNexgenCxAdminPanel: boolean;
  showRecordingLeftPanel: boolean;
  showProgress: boolean;
  previousStatus: UserStatus | null;
  isFreshLogin: boolean;
  initLoading: boolean;
  initError: Error | null;
  profileAvatar: string;

  // Actions
  initFromBootstrap: (validated: WorkplaceBootstrapDto) => void;
  sethasActiveSubscription: (val: boolean) => void;
  setCurrentRoom: (room: WorkplaceState['currentRoom']) => void;
  setHomeroom: (room: WorkplaceState['homeRoom']) => void;
  setApplications: (apps: WorkplaceState['applications']) => void;
  updateSlotLayout: (slot: keyof WorkplaceState['currentSlots'], appUuid: string | null) => void;
  setToggleAdminView: () => void;
  setToggleReportView: () => void;
  setShowLeftPanel: () => void;
  setShowApphubLeftPanel: (show: boolean) => void;
  setShowBookmarkLeftPanel: (show: boolean) => void;
  setShowNexgenCxLeftPanel: (show: boolean) => void;
  setShowNexgenCxAdminPanel: (show: boolean) => void;
  setShowRecordingLeftPanel: (show: boolean) => void;
  setShowOfficeLeftPanel: (show: boolean) => void;
  setShowAuditoriumLeftPanel: (show: boolean) => void;
  addFloatingPanel: (uuid: string, appUuid: string | null) => void;
  updateFloatingPanel: (uuid: string, appUuid: string | null) => void;
  removeFloatingPanel: (uuid: string) => void;
  updateOccupants: (occupants: Occupant[]) => void;
  // loadBackgroundUrl: () => void;
  updateTheme: (newTheme: Partial<WorkplaceState['theme']>) => void;
  setLayoutMode: (mode: 'one-column' | 'two-column') => void;
  setDrawerOpen: (open: boolean) => void;
  setDrawerWidth: (width: number) => void;
  setDrawerView: (data: DrawerView | null) => void;
  setShowRightSidebar: (open: boolean) => void;
  setShowMobileSideMenu: (open: boolean) => void;
  updatePanelAWidth: (width: number) => void;
  updateAppSelection: (appId: string, viewId: string) => void;
  setShowProgress: (show: boolean) => void;
  setDisplayTimezone: (timezone: string) => void;
  getDisplayTimezone: () => string;
  addGuestUser: (guest: Guest[]) => void;
  addSingleGuestUser: (guest: Guest) => void;
  removeGuestUser: (pid: string) => void;
  goHome: (options?: { skipMeetingJoin?: boolean }) => void;
  getUserStatus: () => string;
  setUserStatus: (status: UserStatus) => void;
  setFreshLogin: (val: boolean) => void;
  setInitLoading: (val: boolean) => void;
  setInitError: (err: Error | null) => void;
  getUserContext: () => UserContext;
  canLockCurrentRoom: () => boolean;
  canTakeBreakFromCurrentRoom: () => boolean;
  setRoomLocked: (locked: boolean) => void;
  updateUserLocation: (update: { user_pid: string; room_pid: string; floor_pid: string }) => void;
  clearWorkplaceState: () => void;
  setProfileAvatar: (data: string) => void;
  getFullName: () => string;
  setHasInitialize: (value: boolean) => void;
  dismissDrawerView: () => void;
  fetchOrgSettings: () => Promise<void>;
  setOrgSettings: (settings: Record<string, any>) => void;
}

// ---- Store Definition ----
export const useWorkplaceStore = create<WorkplaceState>()(
  persist(
    (set, get) => ({
      isIdle: false,
      hasInitialize: false,
      org: { pid: '', name: '', slug: '', timezone: '', hasActiveSubscription: true, policies: [] },
      orgSettings: {
        'live-caption-settings': {
          speechModel: 'universal-streaming-english',
          translationEnabled: false,
        },
      },
      profile: {
        pid: '',
        roles: [],
        org_pid: '',
        first_name: '',
        last_name: '',
        avatar_url: DEFAULT_AVATAR,
        timezone: '',
        licenses: [],
        hasPlatformAccess: false,
      },
      presence: {
        status: 'available',
        user_pid: '',
        room_pid: '',
        floor_pid: '',
        floor_name: '',
        room_name: '',
        room_type: 'workstation',
        background_url: '',
      },
      user: { pid: '', name: '', avatar_url: '', timezone: '', status: '' },
      currentRoom: {
        pid: '',
        name: '',
        floor_pid: '',
        background_url: DEFAULT_BG,
        occupants: [],
        displayName: '',
      },
      homeRoom: { pid: '', name: '', floor_pid: '', background_url: DEFAULT_BG, occupants: [] },
      applications: [],
      currentSlots: { full: null, panelA: null, panelB: null, drawer: null },
      floatingPanels: {},
      appSelections: {},
      layoutMode: 'one-column',
      drawerOpen: false,
      drawerWidth: 0,
      panelAWidth: 0,
      drawerView: null,
      theme: {
        topbar: { backgroundColor: '#90887E', backgroundOpacity: 0.9, text: '#FFF' },
        appbar: { backgroundColor: '#90887E', backgroundOpacity: 0.9, text: '#FFF' },
      },
      showRightSidebar: true,
      showMobileSideMenu: false,
      showAdminView: false,
      showReportsView: false,
      showLeftPanel: false,
      showApphubLeftPanel: true,
      showBookmarkLeftPanel: false,
      showNexgenCxLeftPanel: false,
      showNexgenCxAdminPanel: false,
      showRecordingLeftPanel: false,
      showOfficeLeftPanel: false,
      showAuditoriumLeftPanel: false,
      showProgress: false,
      guestUsers: [],
      displayTimezone: '',
      previousStatus: null,
      isFreshLogin: false,
      initLoading: false,
      initError: null,
      profileAvatar: '',

      getDisplayTimezone: () => {
        const state = get();
        return (
          state.user?.timezone ||
          state.org?.timezone ||
          state.displayTimezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'America/New_York'
        );
      },

      setDisplayTimezone: (tz) => {
        set(() => ({ displayTimezone: tz }));
      },

      initFromBootstrap: (validated) => {
        const { profile, presence, org } = validated;
        const isFresh = get().isFreshLogin;
        const userName = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();

        if (isPostHogEnabled && profile.pid) {
          posthog.identify(profile.pid, userName ? { name: maskName(userName) } : undefined);
          if (org.pid) posthog.group('organization', org.pid, { name: org.name });
        }

        // Always seed currentRoom from presence — the home-room placement (on fresh
        // login / session-replaced reload) is handled by WorkplaceService.navigateToRoom,
        // which requires currentRoom != target to actually run the full navigation flow
        // (broadcast, leave old meeting, join new meeting, update floor presence).
        const currentRoomPid = presence.room_pid;
        const currentFloorPid = presence.floor_pid;

        // Preserve a thumbnail already resolved for this same user (e.g. set by the
        // user-profile/me fetch) when the bootstrap profile lacks one — otherwise a
        // re-run of bootstrap resets it to null after it was already available.
        const prevUser = get().user;
        const avatarThumbnailUrl =
          profile.avatar_thumbnail_url ??
          (prevUser.pid === profile.pid ? (prevUser.avatar_thumbnail_url ?? null) : null);

        // Update currentFloorPresence in MultiFloorStore during initialization
        if (currentFloorPid) {
          useMultiFloorStateStore.getState().setCurrentFloorPresence(currentFloorPid);
          Logger.debug('[WorkplaceStore] 📍 Initialized currentFloorPresence:', currentFloorPid);
        }

        set(() => ({
          profile,
          presence,
          user: {
            pid: profile.pid,
            name: `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim(),
            avatar_url: profile.avatar_url ?? DEFAULT_AVATAR,
            avatar_thumbnail_url: avatarThumbnailUrl,
            timezone: profile.timezone ?? '',
            status: isFresh ? 'active' : ((presence.status as UserStatus) ?? 'active'),
          },
          currentRoom: {
            pid: currentRoomPid ?? '',
            name: presence.room_name ?? '',
            background_url: presence?.background_url ?? DEFAULT_BG,
            floor_pid: currentFloorPid ?? '',
            occupants: [],
            displayName: presence.room_name ?? '',
            type: presence.room_type ?? '',
          },
          homeRoom: {
            pid: profile.home_room_pid ?? presence.room_pid ?? '',
            name: presence.room_name ?? '',
            background_url: presence?.background_url ?? DEFAULT_BG,
            floor_pid: profile.home_floor_pid ?? presence.floor_pid ?? '',
            occupants: [],
          },
          org: {
            pid: org.pid,
            name: org.name,
            slug: org.slug,
            timezone: org.timezone ?? '',
            hasActiveSubscription: org.hasActiveSubscription ?? true,
            policies: org.policies ?? [],
          },
          isFreshLogin: false, // Always reset this after bootstrap
          previousStatus: !isFresh && presence.status === 'locked' ? 'active' : null,
        }));

        // Sync lock status with myFloorStore after bootstrap
        // Use setTimeout to ensure the state update is complete before syncing
        setTimeout(() => {
          try {
            useMyFloorStore.getState().syncLockStatusFromWorkplace();
          } catch (error) {
            console.warn('[WorkplaceStore] Could not sync lock status with myFloorStore:', error);
          }
        }, 0);
      },

      sethasActiveSubscription: (val: boolean) =>
        set((state) => ({
          org: {
            ...state.org,
            hasActiveSubscription: val ?? true,
          },
        })),

      setCurrentRoom: (room) => {
        set((state) => {
          return {
            currentRoom: {
              ...room,
              background_url: room.background_url || DEFAULT_BG,
            },
            presence: {
              ...state.presence,
              room_pid: room.pid,
              room_name: room.name || room.displayName || state.presence.room_name,
              floor_pid: room.floor_pid,
              background_url: room.background_url || DEFAULT_BG,
              room_type: (room as any).type || state.presence.room_type,
            },
          };
        });
      },

      setHomeroom: (room) =>
        set(() => ({
          homeRoom: {
            ...room,
            background_url: room.background_url || DEFAULT_BG,
          },
        })),
      getFullName: () => {
        if (get().profile) {
          return `${get().profile?.first_name ?? ''} ${get().profile?.last_name ?? ''}`;
        }
        return '';
      },
      setApplications: (apps) => set(() => ({ applications: apps })),
      updateSlotLayout: (slot, appUuid) =>
        set((state) => ({ currentSlots: { ...state.currentSlots, [slot]: appUuid } })),
      setToggleAdminView: () => set((state) => ({ showAdminView: !state.showAdminView })),
      setToggleReportView: () => set((state) => ({ showReportsView: !state.showReportsView })),
      setShowLeftPanel: () => set((state) => ({ showLeftPanel: !state.showLeftPanel })),
      setShowApphubLeftPanel: (show: boolean) => set(() => ({ showApphubLeftPanel: show })),
      setShowBookmarkLeftPanel: (show: boolean) => set(() => ({ showBookmarkLeftPanel: show })),
      setShowNexgenCxLeftPanel: (show: boolean) => set(() => ({ showNexgenCxLeftPanel: show })),
      setShowNexgenCxAdminPanel: (show: boolean) => set(() => ({ showNexgenCxAdminPanel: show })),
      setShowRecordingLeftPanel: (show: boolean) => set(() => ({ showRecordingLeftPanel: show })),
      setShowOfficeLeftPanel: (show: boolean) => set(() => ({ showOfficeLeftPanel: show })),
      setShowAuditoriumLeftPanel: (show: boolean) => set(() => ({ showAuditoriumLeftPanel: show })),
      setDrawerOpen: (open) => set(() => ({ drawerOpen: open })),
      setDrawerWidth: (width) => set(() => ({ drawerWidth: width })),
      setDrawerView: (data) => set(() => ({ drawerView: data ? { ...data } : null })),
      setShowRightSidebar: (open) => set(() => ({ showRightSidebar: open })),
      setShowMobileSideMenu: (open) => set(() => ({ showMobileSideMenu: open })),
      setShowProgress: (show) => set(() => ({ showProgress: show })),
      dismissDrawerView: () => set(() => ({ drawerView: null, drawerWidth: 0, drawerOpen: false })),

      addFloatingPanel: (uuid, appUuid) =>
        set((state) => ({ floatingPanels: { ...state.floatingPanels, [uuid]: appUuid } })),
      updateFloatingPanel: (uuid, appUuid) =>
        set((state) => ({ floatingPanels: { ...state.floatingPanels, [uuid]: appUuid ?? null } })),
      removeFloatingPanel: (uuid) =>
        set((state) => {
          const updated = { ...state.floatingPanels };
          delete updated[uuid];
          return { floatingPanels: updated };
        }),

      updateOccupants: (occupants) =>
        set((state) => ({ currentRoom: { ...state.currentRoom, occupants } })),

      // loadBackgroundUrl: async () => {
      //   const url = await WorkplaceService.getBackgroundImage();
      //   set((state) => ({
      //     currentRoom: { ...state.currentRoom, background_url: url },
      //   }));
      // },

      removeGuestUser: (pid) => {
        set((state) => ({
          guestUsers: state.guestUsers.filter((guest) => guest.guest_pid !== pid),
        }));
      },
      addGuestUser: (guest: Guest[]) => {
        set({ guestUsers: Array.isArray(guest) ? guest.filter(isHostManagedGuest) : guest });
      },
      addSingleGuestUser: (guest: Guest) => {
        Logger.debug('🚀 ~ guest:', guest);
        if (!isHostManagedGuest(guest)) return;
        set((state) => {
          // Check if guest already exists to avoid duplicates
          const existingGuestIndex = state.guestUsers.findIndex(
            (g) => g.guest_pid === guest.guest_pid
          );
          if (existingGuestIndex !== -1) {
            // Update existing guest
            const updatedGuests = [...state.guestUsers];
            updatedGuests[existingGuestIndex] = guest;
            return { guestUsers: updatedGuests };
          } else {
            // Add new guest
            return { guestUsers: [...state.guestUsers, guest] };
          }
        });
      },
      updateTheme: (newTheme) => set((state) => ({ theme: { ...state.theme, ...newTheme } })),

      setLayoutMode: (mode) => {
        const slotSelections = useAppSelectionStore.getState().slotSelections;
        let restored;
        if (mode === 'one-column') {
          restored = {
            full: slotSelections.full?.appId ?? null,
            panelA: null,
            panelB: null,
          };
        } else {
          restored = {
            full: null,
            panelA: slotSelections.panelA?.appId ?? null,
            panelB: slotSelections.panelB?.appId ?? null,
          };
        }
        set(() => ({ layoutMode: mode, currentSlots: { ...restored, drawer: null } }));
      },

      updatePanelAWidth: (width) => set(() => ({ panelAWidth: width })),
      updateAppSelection: (appId, viewId) =>
        set((state) => ({
          appSelections: { ...state.appSelections, [appId]: viewId },
        })),

      goHome: (options?: { skipMeetingJoin?: boolean }) => {
        const state = get();
        const userId = state.user.pid;
        const roomId = state.profile.home_room_pid ?? state.homeRoom.pid;
        const floorId = state.profile.home_floor_pid ?? state.homeRoom.floor_pid;
        const currentFloorPlace = useMultiFloorStateStore
          .getState()
          .getFloorState(floorId || '')?.place;
        const userRoom: any = currentFloorPlace?.rooms?.find((room) => room.pid === roomId);

        const getChatUsername = useChatStore.getState().getChatUsername;
        const moveToDirectChat = useChatStore.getState().moveToDirectChat;
        const username = getChatUsername(userRoom?.room_owners[0].user.email);
        moveToDirectChat(username);

        if (floorId && roomId && userId) {
          OfficeService.getInstance().navigateToRoom({
            floorPid: floorId,
            roomPid: roomId,
            userPid: userId,
            room: userRoom,
            skipMeetingJoin: options?.skipMeetingJoin,
            source: 'go_home',
          });
        }
      },
      setFreshLogin: (val) => set(() => ({ isFreshLogin: val })),

      setInitLoading: (val) => set(() => ({ initLoading: val })),

      setInitError: (err) => set(() => ({ initError: err })),
      setProfileAvatar: (data: string) => set(() => ({ profileAvatar: data })),

      setHasInitialize: (value: boolean) => {
        set(() => ({
          hasInitialize: value,
        }));
      },

      fetchOrgSettings: async () => {
        try {
          const response = await ApiService.get<Record<string, any>>(
            'org-settings?keys=live-caption-settings'
          );

          if (response && response['live-caption-settings']) {
            const settings = {
              speechModel:
                response['live-caption-settings'].speechModel || 'universal-streaming-english',
              translationEnabled: response['live-caption-settings'].translationEnabled || false,
              translationLanguages: response['live-caption-settings'].translationLanguages ?? [],
            };

            set((state) => ({
              orgSettings: {
                ...state.orgSettings,
                'live-caption-settings': settings,
              },
            }));
          } else {
            // Backend filtered it out (org doesn't have access) - explicitly reset to defaults
            set((state) => ({
              orgSettings: {
                ...state.orgSettings,
                'live-caption-settings': {
                  speechModel: 'universal-streaming-english',
                  translationEnabled: false,
                },
              },
            }));
          }
        } catch (error) {
          console.error('[LiveCaption] Failed to fetch org settings:', error);
          console.log(
            '[LiveCaption] Resetting to default settings: english-only model, translation disabled'
          );
          // Reset to defaults on error
          set((state) => ({
            orgSettings: {
              ...state.orgSettings,
              'live-caption-settings': {
                speechModel: 'universal-streaming-english',
                translationEnabled: false,
              },
            },
          }));
        }
      },

      setOrgSettings: (settings: Record<string, any>) => {
        set({ orgSettings: settings });
      },

      getUserStatus() {
        const state = get();
        return state.user.status || 'available';
      },

      setUserStatus: (status: UserStatus) => {
        set((state) => ({
          user: { ...state.user, status },
          presence: { ...state.presence, status },
        }));

        // Sync lock status with myFloorStore when user status changes
        // This ensures the lock icon reflects the actual user status
        useMyFloorStore.getState().syncLockStatusFromWorkplace(undefined, status);
      },

      getUserContext: () => {
        const state = get();

        //Logger.debug('TBL state in getUserContext:', state);

        // Get floor_pid with fallback: currentRoom -> presence -> homeRoom
        const floorPid =
          state?.currentRoom?.floor_pid ||
          state?.presence?.floor_pid ||
          state?.homeRoom?.floor_pid ||
          '';

        return {
          user_pid: state?.user?.pid || '',
          room_pid: state?.currentRoom?.pid || '',
          floor_pid: floorPid,
          org_pid: state?.org?.pid || '',
        };
      },

      canLockCurrentRoom: () => {
        const state = get();
        const { currentRoom, homeRoom, presence } = state;

        if (!homeRoom.pid) {
          // Logger.debug('amit1111', state);
          return false; // No home room assigned
        }

        const isLobbyType = currentRoom.type?.toLowerCase() === 'lobby';
        const ispresenceRoomType = presence.room_type?.toLowerCase() === 'lobby';
        const isLobbyName = currentRoom.name?.toLowerCase().includes('lobby');

        if (isLobbyType || isLobbyName || ispresenceRoomType) {
          return false;
        }

        const isHomeRoom =
          state.currentRoom.pid === state.homeRoom.pid && state.homeRoom.pid !== '';
        const isConferenceRoom = currentRoom.type?.toLowerCase() === 'conference';
        return isConferenceRoom || isHomeRoom;
      },

      canTakeBreakFromCurrentRoom: () => {
        const state = get();
        return state.currentRoom.pid === state.homeRoom.pid && state.homeRoom.pid !== '';
      },

      setRoomLocked: async (locked: boolean) => {
        const state = get();
        if (!state.canLockCurrentRoom()) {
          console.warn('Cannot lock room: User can only lock their home room');
          return;
        }

        // Store previous status before changing to locked
        if (locked && state.user.status !== 'locked') {
          set({ previousStatus: state.user.status as UserStatus });
        }

        // Set user status to 'locked' when room is locked, restore previous status when unlocked
        const newStatus = locked ? 'locked' : state.previousStatus || 'active';
        const statusMessage = locked ? 'Room locked' : '';

        Logger.debug(
          `[WorkplaceStore] 🔄 Setting room ${locked ? 'locked' : 'unlocked'}: ${newStatus}`
        );

        try {
          // 1. First persist to backend to ensure consistency
          await ApiService.request({
            method: 'PATCH',
            endpoint: '/workplace/user/presence',
            data: {
              status: newStatus,
              message: statusMessage,
            },
            service: 'app',
          });
          Logger.debug(`[WorkplaceStore] ✅ Status change persisted to backend: ${newStatus}`);

          // 2. Then update local state
          state.setUserStatus(newStatus as UserStatus);

          // Sync lock status with myFloorStore
          useMyFloorStore.getState().syncLockStatusFromWorkplace(undefined, newStatus);

          // Update MultiFloorStore directly for the current user — own socket events are
          // skipped by WorkplaceEventRouter, so the floor map would otherwise never turn red
          const userContext = state.getUserContext();
          if (userContext.floor_pid && userContext.user_pid) {
            const multiFloorStore = useMultiFloorStateStore.getState();
            multiFloorStore.updateUserStatus(
              userContext.floor_pid,
              userContext.user_pid,
              newStatus,
              statusMessage
            );

            // On unlock, any visitor who was locked alongside us in this room also needs
            // resetting locally. Their own client gets this via the UserStatusChanged topic
            // event, but that event is filtered out on our own client (self-events are
            // skipped), so without this their ring would stay red on our screen only.
            if (!locked && userContext.room_pid) {
              const floorState = multiFloorStore.getFloorState(userContext.floor_pid);
              const otherLockedUsers = (floorState?.presence?.users || []).filter(
                (u: any) =>
                  u.room === userContext.room_pid &&
                  u.status === 'locked' &&
                  u.pid !== userContext.user_pid
              );
              otherLockedUsers.forEach((u: any) => {
                multiFloorStore.updateUserStatus(
                  userContext.floor_pid,
                  u.pid,
                  newStatus,
                  statusMessage
                );
              });

              // Guests live in a separate presence.guests array — without this, a guest
              // summoned into the room would stay stuck red on the host's own screen too.
              // Not gated on status === 'locked': a guest summoned into an already-locked
              // room never had this entry marked 'locked' client-side to begin with, so
              // guests only ever being active/locked, it's safe to always sync them here.
              const guestsInRoom = (floorState?.presence?.guests || []).filter(
                (g: any) => g.room === userContext.room_pid
              );
              guestsInRoom.forEach((g: any) => {
                multiFloorStore.updateGuestPresence(userContext.floor_pid, {
                  ...g,
                  status: newStatus,
                  message: statusMessage,
                } as any);
              });
            }
          }

          // 3. Finally emit event to notify other users with a small delay to ensure backend is updated
          setTimeout(() => {
            const userContext = state.getUserContext();
            WorkplaceEventRouter.emitUserStatusChanged({
              user_pid: userContext.user_pid,
              status: newStatus as UserStatus,
              org_pid: userContext.org_pid,
              floor_pid: userContext.floor_pid,
              room_pid: userContext.room_pid,
              message: statusMessage,
            });
            Logger.debug(`[WorkplaceStore] 📡 Status change event emitted: ${newStatus}`);
          }, 50);
        } catch (error) {
          console.error('[WorkplaceStore] ❌ Failed to change room lock status:', error);
          // Don't update local state if backend call failed
          throw error;
        }
      },

      updateUserLocation: (update: { user_pid: string; room_pid: string; floor_pid: string }) => {
        Logger.debug('[WorkplaceStore] 📍 Updating user location:', update);

        const state = get();

        const currentRoomPid = state.currentRoom.pid;
        const currentOccupants = state.currentRoom.occupants || [];

        const wasInCurrentRoom = currentOccupants.some(
          (occupant) => occupant.pid === update.user_pid
        );
        const isMovingToCurrentRoom = update.room_pid === currentRoomPid;

        if (wasInCurrentRoom && !isMovingToCurrentRoom) {
          const updatedOccupants = currentOccupants.filter(
            (occupant) => occupant.pid !== update.user_pid
          );
          set((state) => ({
            currentRoom: {
              ...state.currentRoom,
              occupants: updatedOccupants,
            },
          }));
          Logger.debug('[WorkplaceStore] 📍 User left current room, updated occupants');
        } else if (!wasInCurrentRoom && isMovingToCurrentRoom) {
          // User is joining our current room - we'd need user info to add them
          // For now, just trigger a refresh by updating the timestamp
          set((state) => ({
            currentRoom: {
              ...state.currentRoom,
              // Force a re-render by updating a timestamp or similar
              lastUpdated: Date.now(),
            },
          }));
          Logger.debug('[WorkplaceStore] 📍 User joined current room, triggered refresh');
        }

        // Trigger any other UI updates that might be needed
        // This could include updating user lists, presence indicators, etc.
        Logger.debug('[WorkplaceStore] ✅ User location update processed');
      },

      // Complete cleanup of workplace state
      clearWorkplaceState: () => {
        // Clear MultiFloorStore and FloorManagerStore FIRST to prevent cross-user contamination
        try {
          useMultiFloorStateStore.getState().resetState();
          useFloorManagerStore.getState().resetState();
        } catch (error) {
          console.error('[WorkplaceStore] ❌ Error clearing floor stores:', error);
        }

        // Then reset workplace state
        set(() => ({
          isIdle: false,
          org: {
            pid: '',
            name: '',
            slug: '',
            timezone: '',
            hasActiveSubscription: true,
            policies: [],
          },
          profile: {
            pid: '',
            roles: [],
            org_pid: '',
            first_name: '',
            last_name: '',
            avatar_url: DEFAULT_AVATAR,
            timezone: '',
            licenses: [],
            hasPlatformAccess: false,
          },
          presence: {
            status: 'offline',
            user_pid: '',
            room_pid: '',
            floor_pid: '',
            floor_name: '',
            room_name: '',
            room_type: 'workstation',
            background_url: '',
          },
          user: { pid: '', name: '', avatar_url: '', timezone: '', status: '' },
          currentRoom: {
            pid: '',
            name: '',
            floor_pid: '',
            background_url: DEFAULT_BG,
            occupants: [],
            displayName: '',
          },
          homeRoom: { pid: '', name: '', floor_pid: '', background_url: DEFAULT_BG, occupants: [] },
          applications: [],
          currentSlots: { full: null, panelA: null, panelB: null, drawer: null },
          floatingPanels: {},
          appSelections: {},
          layoutMode: 'one-column',
          drawerOpen: false,
          drawerWidth: 0,
          panelAWidth: 0,
          drawerView: null,
          theme: {
            topbar: { backgroundColor: '#90887E', backgroundOpacity: 0.9, text: '#FFF' },
            appbar: { backgroundColor: '#90887E', backgroundOpacity: 0.9, text: '#FFF' },
          },
          showRightSidebar: true,
          showMobileSideMenu: false,
          showAdminView: false,
          showLeftPanel: false,
          showApphubLeftPanel: false,
          showBookmarkLeftPanel: false,
          showNexgenCxLeftPanel: false,
          showNexgenCxAdminPanel: false,
          showOfficeLeftPanel: false,
          showAuditoriumLeftPanel: false,
          showProgress: false,
          guestUsers: [],
          displayTimezone: '',
          previousStatus: null,
          isFreshLogin: false,
          initLoading: false,
          initError: null,
          hasInitialize: false,
        }));
      },
    }),
    {
      name: 'workplace-store',
      partialize: (state) => ({
        layoutMode: state.layoutMode,
        drawerWidth: state.drawerWidth,
        panelAWidth: state.panelAWidth,
        appSelections: state.appSelections,
        currentSlots: state.currentSlots,
        floatingPanels: state.floatingPanels,
        profile: state.profile,
        user: {
          pid: state.user.pid,
          name: state.user.name,
          avatar_url: state.user.avatar_url,
          avatar_thumbnail_url: state.user.avatar_thumbnail_url,
          timezone: state.user.timezone,
        },
        showApphubLeftPanel: state.showApphubLeftPanel,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<WorkplaceState>;
        return {
          ...currentState,
          ...persisted,
          user: {
            ...currentState.user,
            ...(persisted.user ?? {}),
            // Never restore presence status from localStorage — bootstrap/API is the source of truth.
            status: '',
          },
        };
      },
    }
  )
);

export const isSelfUserPid = (userPid?: string) => {
  return useWorkplaceStore.getState().user?.pid === userPid;
};

// Re-export constants for backward compatibility
export { DEFAULT_AVATAR, DEFAULT_BG } from '@host/workplace/services/WorkplaceService';
